import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  EnvelopeIcon,
  LinkIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";

interface GmailMessage {
  id: string;
  subject: string;
  from: string;
  date: string;
  snippet: string;
  unread?: boolean;
}

interface EmailAppProps {
  currentUser?: { role?: string };
}

export const EmailApp: React.FC<EmailAppProps> = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [messages, setMessages] = useState<GmailMessage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ body?: string; subject?: string; from?: string } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin";

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const st = await apiService.getGmailStatus();
      const data = st.data as { connected?: boolean; configured?: boolean } | undefined;
      setConnected(!!data?.connected);
      setConfigured(!!data?.configured);
      if (data?.connected) {
        const list = await apiService.getGmailMessages(searchQuery || undefined);
        const listData = list.data as { messages?: GmailMessage[] } | undefined;
        if (list.error) setError(list.error);
        else setMessages(listData?.messages || []);
      } else {
        setMessages([]);
      }
    } catch {
      setError("No se pudo cargar el correo");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    loadStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get("gmail") === "connected") {
      params.delete("gmail");
      const qs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  }, [loadStatus]);

  useEffect(() => {
    if (!selectedId || !connected) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    apiService.getGmailMessage(selectedId).then((res) => {
      if (cancelled) return;
      const d = res.data as { body?: string; subject?: string; from?: string } | undefined;
      setDetail(d || null);
      setDetailLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId, connected]);

  const selectedIndex = messages.findIndex((m) => m.id === selectedId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") {
        if (e.key === "Escape" && composeOpen) setComposeOpen(false);
        return;
      }
      if (e.key === "c" && !composeOpen) {
        e.preventDefault();
        setComposeOpen(true);
      }
      if (e.key === "/" && !composeOpen) {
        e.preventDefault();
        document.getElementById("email-search")?.focus();
      }
      if (!messages.length) return;
      if (e.key === "j") {
        e.preventDefault();
        const next = Math.min(selectedIndex + 1, messages.length - 1);
        setSelectedId(messages[next].id);
      }
      if (e.key === "k") {
        e.preventDefault();
        const prev = Math.max(selectedIndex - 1, 0);
        setSelectedId(messages[prev].id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [messages, selectedIndex, composeOpen]);

  const handleConnect = async () => {
    if (!isAdmin) return;
    const res = await apiService.getGmailAuthUrl();
    const data = res.data as { authUrl?: string } | undefined;
    if (data?.authUrl) window.location.href = data.authUrl;
    else setError(res.error || "No se pudo conectar");
  };

  const handleSend = async () => {
    setSending(true);
    const res = await apiService.sendGmailMessage(compose);
    setSending(false);
    if (res.error) setError(res.error);
    else {
      setComposeOpen(false);
      setCompose({ to: "", subject: "", body: "" });
      await loadStatus();
    }
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-8 w-48 bg-zinc-200 dark:bg-zinc-800 rounded-lg" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-[480px]">
          <div className="rounded-xl bg-zinc-200 dark:bg-zinc-800" />
          <div className="rounded-xl bg-zinc-200 dark:bg-zinc-800 hidden lg:block" />
        </div>
      </div>
    );
  }

  if (!configured) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 px-6 rounded-2xl border border-dashed border-zinc-300 dark:border-zinc-700">
        <EnvelopeIcon className="h-12 w-12 mx-auto text-zinc-400 mb-4" />
        <h2 className="text-lg font-semibold">Configura Google OAuth</h2>
        <p className="mt-2 text-sm text-zinc-500">
          Ve a Ajustes → Google Drive y guarda el Client ID (y Secret una vez) para habilitar Gmail.
        </p>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 px-6 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-[#141416] shadow-sm">
        <EnvelopeIcon className="h-12 w-12 mx-auto text-primary-500 mb-4" />
        <h2 className="text-lg font-semibold">Conecta tu Gmail</h2>
        <p className="mt-2 text-sm text-zinc-500 mb-6">
          Bandeja de lectura y envío con la API de Gmail. Atajos: j/k navegar, c redactar, / buscar.
        </p>
        {isAdmin ? (
          <button
            type="button"
            onClick={handleConnect}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary-700"
          >
            <LinkIcon className="h-4 w-4" />
            Conectar cuenta Google
          </button>
        ) : (
          <p className="text-sm text-zinc-500">Solo administradores pueden conectar Gmail.</p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-h-[720px]">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold tracking-tight">Correo</h1>
        <button
          type="button"
          onClick={() => setComposeOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-primary-600 text-white text-sm font-medium px-4 py-2 hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4" />
          Redactar
        </button>
      </div>
      {error && (
        <p className="mb-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-lg">{error}</p>
      )}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-0 min-h-0 rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-[#141416] shadow-sm overflow-hidden">
        <div className="lg:col-span-2 flex flex-col border-r border-zinc-100 dark:border-zinc-800 min-h-0">
          <div className="p-3 border-b border-zinc-100 dark:border-zinc-800">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <input
                id="email-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setSearchQuery(search)}
                placeholder="Buscar (Enter)…"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 outline-none focus:ring-2 focus:ring-primary-500/30"
              />
            </div>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto">
            {messages.length === 0 ? (
              <p className="p-6 text-sm text-zinc-500 text-center">Bandeja vacía</p>
            ) : (
              messages.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSelectedId(m.id)}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-50 dark:border-zinc-800/80 transition-colors ${
                    selectedId === m.id ? "bg-primary-50 dark:bg-primary-950/30" : "hover:bg-zinc-50 dark:hover:bg-zinc-900/50"
                  }`}
                >
                  <div className="flex justify-between gap-2">
                    <span className={`text-sm truncate ${m.unread ? "font-semibold" : ""}`}>{m.from}</span>
                    <span className="text-[10px] text-zinc-400 shrink-0">{m.date?.slice(0, 12)}</span>
                  </div>
                  <p className="text-sm truncate mt-0.5">{m.subject}</p>
                  <p className="text-xs text-zinc-500 truncate mt-0.5">{m.snippet}</p>
                </button>
              ))
            )}
          </div>
        </div>
        <div className="lg:col-span-3 flex flex-col min-h-0 hidden lg:flex">
          {!selectedId ? (
            <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
              Selecciona un mensaje (j / k)
            </div>
          ) : detailLoading ? (
            <div className="flex-1 p-6 space-y-3 animate-pulse">
              <div className="h-6 w-2/3 bg-zinc-200 dark:bg-zinc-800 rounded" />
              <div className="h-4 w-1/3 bg-zinc-200 dark:bg-zinc-800 rounded" />
              <div className="h-32 bg-zinc-200 dark:bg-zinc-800 rounded-xl mt-4" />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-6">
              <h2 className="text-lg font-semibold">{detail?.subject || messages.find((m) => m.id === selectedId)?.subject}</h2>
              <p className="text-sm text-zinc-500 mt-1">{detail?.from}</p>
              <pre className="mt-6 text-sm whitespace-pre-wrap font-sans text-zinc-700 dark:text-zinc-300 leading-relaxed">
                {detail?.body || messages.find((m) => m.id === selectedId)?.snippet}
              </pre>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {composeOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-md rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-[#141416] shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
              <span className="font-medium text-sm">Nuevo mensaje</span>
              <button type="button" onClick={() => setComposeOpen(false)} className="p-1 rounded hover:bg-zinc-200 dark:hover:bg-zinc-800">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <input
                placeholder="Para"
                value={compose.to}
                onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
                className="w-full text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent"
              />
              <input
                placeholder="Asunto"
                value={compose.subject}
                onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                className="w-full text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent"
              />
              <textarea
                placeholder="Mensaje"
                rows={5}
                value={compose.body}
                onChange={(e) => setCompose((c) => ({ ...c, body: e.target.value }))}
                className="w-full text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 bg-transparent resize-none"
              />
              <button
                type="button"
                disabled={sending}
                onClick={handleSend}
                className="w-full inline-flex justify-center items-center gap-2 rounded-xl bg-primary-600 text-white py-2.5 text-sm font-semibold disabled:opacity-50"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {sending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
